// Draft core points while a zone customize session is open.
// A saved set is only a same-id stand-in until `/layers` returns `zone-cores-<version>`
// (`category: zone_cores`). That manifest row is the record; `forgetServed` drops the copy.
import { create } from "zustand";
import type { ZoneCore, ZoneCorePoint } from "../lib/zoneCores";

export interface SavedZoneCores {
  id: string;
  fieldId: string;
  createdAt: string;
  cores: ZoneCore[];
}

const DRAFT_KEY = (fieldId: string) => `sm_zone_cores_draft:${fieldId}`;
const SAVED_KEY = (fieldId: string) => `sm_zone_cores:${fieldId}`;

function loadDraft(fieldId: string): ZoneCorePoint[] {
  try {
    const raw = JSON.parse(
      localStorage.getItem(DRAFT_KEY(fieldId)) ?? "[]",
    ) as ZoneCorePoint[];
    return Array.isArray(raw)
      ? raw.filter((p) => Number.isFinite(p?.lng) && Number.isFinite(p?.lat))
      : [];
  } catch {
    return [];
  }
}
function loadSaved(fieldId: string): SavedZoneCores[] {
  try {
    const raw = JSON.parse(
      localStorage.getItem(SAVED_KEY(fieldId)) ?? "[]",
    ) as SavedZoneCores[];
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

interface ZoneCoreState {
  pickingFieldId: string | null;
  drafts: Record<string, ZoneCorePoint[]>;
  saved: Record<string, SavedZoneCores[]>;
  ensure: (fieldId: string) => void;
  togglePicking: (fieldId: string) => void;
  stopPicking: () => void;
  place: (fieldId: string, lng: number, lat: number) => void;
  moveAt: (fieldId: string, index: number, lng: number, lat: number) => void;
  setPoints: (fieldId: string, points: ZoneCorePoint[]) => void;
  removeAt: (fieldId: string, index: number) => void;
  clearDraft: (fieldId: string) => void;
  commit: (
    fieldId: string,
    cores: ZoneCore[],
    versionId?: string | null,
  ) => string;
  forgetServed: (fieldId: string, versionIds: string[]) => void;
}

function writeDraft(fieldId: string, points: ZoneCorePoint[]) {
  try {
    localStorage.setItem(DRAFT_KEY(fieldId), JSON.stringify(points));
  } catch {
    /* quota */
  }
}
function writeSaved(fieldId: string, sets: SavedZoneCores[]) {
  try {
    localStorage.setItem(SAVED_KEY(fieldId), JSON.stringify(sets));
  } catch {
    /* quota */
  }
}

export const useZoneCoreStore = create<ZoneCoreState>((set, get) => ({
  pickingFieldId: null,
  drafts: {},
  saved: {},
  ensure: (fieldId) => {
    if (get().drafts[fieldId] && get().saved[fieldId]) return;
    set((s) => ({
      drafts: s.drafts[fieldId]
        ? s.drafts
        : { ...s.drafts, [fieldId]: loadDraft(fieldId) },
      saved: s.saved[fieldId]
        ? s.saved
        : { ...s.saved, [fieldId]: loadSaved(fieldId) },
    }));
  },
  togglePicking: (fieldId) =>
    set((s) => ({
      pickingFieldId: s.pickingFieldId === fieldId ? null : fieldId,
    })),
  stopPicking: () => set({ pickingFieldId: null }),
  place: (fieldId, lng, lat) =>
    set((s) => {
      const cur = s.drafts[fieldId] ?? loadDraft(fieldId);
      const next = [...cur, { lng, lat }];
      writeDraft(fieldId, next);
      return { drafts: { ...s.drafts, [fieldId]: next } };
    }),
  moveAt: (fieldId, index, lng, lat) =>
    set((s) => {
      const cur = s.drafts[fieldId] ?? loadDraft(fieldId);
      if (!cur[index]) return s;
      const next = cur.map((p, i) => (i === index ? { lng, lat } : p));
      writeDraft(fieldId, next);
      return { drafts: { ...s.drafts, [fieldId]: next } };
    }),
  setPoints: (fieldId, points) =>
    set((s) => {
      writeDraft(fieldId, points);
      return { drafts: { ...s.drafts, [fieldId]: points } };
    }),
  removeAt: (fieldId, index) =>
    set((s) => {
      const cur = s.drafts[fieldId] ?? loadDraft(fieldId);
      const next = cur.filter((_, i) => i !== index);
      writeDraft(fieldId, next);
      return { drafts: { ...s.drafts, [fieldId]: next } };
    }),
  clearDraft: (fieldId) =>
    set((s) => {
      writeDraft(fieldId, []);
      return {
        drafts: { ...s.drafts, [fieldId]: [] },
        pickingFieldId: s.pickingFieldId === fieldId ? null : s.pickingFieldId,
      };
    }),
  commit: (fieldId, cores, versionId) => {
    const id =
      versionId && versionId.length ? versionId : `${fieldId}-${Date.now()}`;
    const row: SavedZoneCores = {
      id,
      fieldId,
      createdAt: new Date().toISOString(),
      cores,
    };
    set((s) => {
      const cur = s.saved[fieldId] ?? loadSaved(fieldId);
      const next = [row, ...cur.filter((x) => x.id !== id)].slice(0, 20);
      writeSaved(fieldId, next);
      writeDraft(fieldId, []);
      return {
        saved: { ...s.saved, [fieldId]: next },
        drafts: { ...s.drafts, [fieldId]: [] },
        pickingFieldId: s.pickingFieldId === fieldId ? null : s.pickingFieldId,
      };
    });
    return id;
  },
  forgetServed: (fieldId, versionIds) =>
    set((s) => {
      const cur = s.saved[fieldId];
      if (!cur?.length || !versionIds.length) return s;
      const drop = new Set(versionIds);
      const next = cur.filter((row) => !drop.has(row.id));
      if (next.length === cur.length) return s;
      writeSaved(fieldId, next);
      return { saved: { ...s.saved, [fieldId]: next } };
    }),
}));
